import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Form, Overlay, Popover } from 'react-bootstrap';
import { BsDot } from 'react-icons/bs';
import styled from 'styled-components';
import { Tooltip as CustomeToolTip } from '@mui/material';

const TextAreaField = styled(Overlay)`
    height: 32px;
    width: 200px;
    &:hover {
        cursor: pointer;
    }
    `;
// Validation schemas
const singleJobSchema = Yup.object().shape({
  assign_reason: Yup.string().required('Required'),
});

const multiJobSchema = Yup.object().shape({
  jobs: Yup.array().of(
    Yup.object().shape({
      assign_reason: Yup.string().when('is_select', {
        is: true,
        then: (schema) => schema.required('Required'),
        otherwise: (schema) => schema.notRequired(),
      }),
    })
  ),
});

const AssignBackPopover = ({
  show,
  target,
  onHide,
  jobData,
  userStatus,
  onAssignSingle,
  onAssignMultiple,
  subJobOptions,
  setSubJobOptions
}) => {
  const [assignTo, setAssignTo] = useState('user');

  // Single job form
  const singleForm = useForm({
    resolver: yupResolver(singleJobSchema),
    defaultValues: {
      assign_reason: '',
    },
  });
  // Multiple jobs form
  const multiForm = useForm({
      resolver: yupResolver(multiJobSchema),
      defaultValues: {
          jobs: subJobOptions || [],
        },
        
    });
    const {reset,getValues} = multiForm

  const { fields, update } = useFieldArray({
    control: multiForm.control,
    name: 'jobs',
  });

  // Handle single job submission
  const handleSingleSubmit = (data) => {
    const submitData = {
      ...data,
      assign_to: assignTo,
      job: jobData?.job,
    };
    onAssignSingle(submitData);
  };

  // Handle multiple jobs submission
  const handleMultipleSubmit = (data) => {
    const selectedJobs = data.jobs
      .filter(job => job.is_select)
      .map(job => ({
        ...job,
        user: job.assignTo === 'user',
        reason: job.assign_reason || '',
        sub_job: job.value || '',
      }));

    const submitData = {
      job: jobData?.job,
      reason: '',
      user: '',
      is_sub_job_reassign: true,
      data: selectedJobs,
    };

    onAssignMultiple(submitData);
  };

  // Change assignment status for multiple jobs
  const changeUserAssignStatus = (assignType, index) => {
    const currentJob = multiForm.getValues(`jobs.${index}`);
    update(index, { ...currentJob, assignTo: assignType });
  };

  // Reset forms when hiding
  const handleHide = () => {
    setSubJobOptions([])
    singleForm.reset();
    multiForm.reset();
    onHide();
  };
useEffect(()=>{
    if(subJobOptions?.length>0){
        reset({jobs:subJobOptions})
    }
},[subJobOptions,reset])

return (
     <TextAreaField 
        rootClose={true}
        show={show}
        target={target}
        placement="left"
        onHide={handleHide}
    >
        <Popover 
        className={jobData?.is_large_batch_job ? 'pc-popover-assign-large' : 'pc-popover-assign'}
        >
            <Popover.Header as="h3">Assign Back</Popover.Header>
            <Popover.Body>
                {!jobData?.is_large_batch_job ? (
                // Single Job Assignment
                <SingleJobAssignment
                    form={singleForm}
                    assignTo={assignTo}
                    setAssignTo={setAssignTo}
                    userStatus={userStatus}
                    onSubmit={handleSingleSubmit}
                />
                ) : (
                // Multiple Jobs Assignment
                <MultipleJobsAssignment
                    form={multiForm}
                    fields={fields}
                    onSubmit={handleMultipleSubmit}
                    changeUserAssignStatus={changeUserAssignStatus}
                />
                )}
            </Popover.Body>
        </Popover>
    </TextAreaField>
  );
};

// Single Job Assignment Component
const SingleJobAssignment = ({ 
  form, 
  assignTo, 
  setAssignTo, 
  userStatus, 
  onSubmit, 
}) => {
  const { handleSubmit, register, formState: { errors, isValid, isSubmitting } } = form;

  return (
    <>
      <div className="mb-2 d-flex flex-row align-items-center justify-content-around gap-3">
        <div className="flex-shrink-1">Assign to:</div>
        <div 
          className={assignTo === 'user' ? 
            "btn btn-light btn-active-primary flex-grow-1" : 
            "btn btn-light btn-active-secondary flex-grow-1"
          }
          onClick={() => setAssignTo('user')}
          role="button"
        >
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="22" 
            height="22" 
            fill={userStatus?.is_active ? "#38E54D" : "#FF1E1E"} 
            className="bi bi-dot" 
            viewBox="0 0 16 16"
          >
            <path d="M8 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />
          </svg>
          {userStatus?.user || ""}
        </div>
        <div 
          role="button"
          className={assignTo === 'common' ? 
            "btn btn-light btn-active-primary flex-grow-1" : 
            "btn btn-light btn-active-secondary flex-grow-1"
          }
          onClick={() => setAssignTo('common')}
        >
          Common pool
        </div>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Control
          type="text"
          as="textarea"
          {...register('assign_reason')}
          isInvalid={!!errors.assign_reason}
          className="b-r-10 text-uppercase"
          rows={4}
          style={{ resize: 'none', width: '100%' }}
        />
        <Form.Control.Feedback type="invalid">
          {errors.assign_reason?.message}
        </Form.Control.Feedback>
        
        <div className="d-flex flex-row justify-content-end">
          <button 
            type="submit" 
            disabled={!isValid || isSubmitting}
            className="btn btn-reject text-end"
            data-bs-display="static" 
            aria-expanded="false"
          >
            {isSubmitting ? 'Assigning...' : 'Assign'}
          </button>
        </div>
      </Form>
    </>
  );
};

// Multiple Jobs Assignment Component
const MultipleJobsAssignment = ({ 
  form, 
  fields, 
  onSubmit, 
  changeUserAssignStatus, 
}) => {
  const { handleSubmit, register, formState: { errors, isValid, isSubmitting }, watch, setValue, getValues } = form;
  const watchedJobs = watch('jobs');
  
  // State for multi-selection features
  const [assignlargeBatchMulty, setAssignlargeBatchMulty] = useState(false);
  const [showMultiSelectionPopup, setShowMultiSelectionPopup] = useState(false);
  const [multiAssignOption, setMultiAssignOption] = useState(''); // 'common' or 'user'

  // Handle option selection and immediate confirmation
  const handleOptionSelect = (option) => {
    // Set the selected option
    setMultiAssignOption(option);
    
    // Enable multi-selection
    setAssignlargeBatchMulty(true);
    
    // Update all items based on selected option
    const currentJobs = getValues('jobs');
    const updated = currentJobs.map((item) => ({
      ...item,
      is_select: true,
      assignTo: option
    }));
    setValue('jobs', updated);
    
    // Close popup
    setShowMultiSelectionPopup(false);
    
    // Reset popup option for next time
    setMultiAssignOption('');
  };

  // Handle checkbox change
  const handleCheckboxChange = (index, checked) => {
    setValue(`jobs.${index}.is_select`, checked);
    setAssignlargeBatchMulty(false);
  };

  // Handle reason change with batch update logic
  const handleReasonChange = (index, value) => {
    if (assignlargeBatchMulty) {
      const currentJobs = getValues('jobs');
      currentJobs.map((item,ind) => {
        setValue(`jobs.${ind}.assign_reason`, value);
      });
    } else {
      setValue(`jobs.${index}.assign_reason`, value);
    }
  };

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <div className="d-flex flex-column scroll-claim gap-1 pb-2">
        {fields.map((field, index) => {
          const job = watchedJobs[index];
          return (
            <ul key={field.id} className="list-group list-group-horizontal">
              <li className="list-group-item d-flex align-items-center option-item-assign width-check p-1 m-0" style={{ fontSize: '0.8em' }}>
                <Form.Check.Input
                  className="checked-servicelines"
                  type="checkbox"
                  {...register(`jobs.${index}.is_select`)}
                  onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                  checked={job?.is_select || false}
                />
              </li>
              
              <li className="list-group-item d-flex align-items-center option-item-assign width-label p-1 m-0">
                <span style={{ fontSize: '0.9em' }}>
                  {job?.label}
                </span>
              </li>
              
              <li className="list-group-item d-flex align-items-center option-item-assign p-1 m-0">
                <button 
                  type="button"
                  className={job?.assignTo === 'common' ? 
                    "btn btn-light btn-sm btn-icon btn-active-primary width-common-btn fs-7" : 
                    "btn btn-light btn-sm btn-icon btn-active-secondary width-common-btn fs-7"
                  }
                  onClick={() => changeUserAssignStatus('common', index)}
                >
                  Common Pool
                </button>
              </li>
              
              <li className="list-group-item d-flex align-items-center justify-content-center option-item-assign p-1 m-0">
                <button 
                  type="button"
                  className={job?.assignTo === 'user' ? 
                    "btn btn-light btn-active-primary btn-sm btn-icon width-user-btn" : 
                    "btn btn-light btn-sm btn-icon btn-active-secondary width-user-btn"
                  }
                  style={{ fontSize: '0.8em' }}
                  onClick={() => changeUserAssignStatus('user', index)}
                >
                  <BsDot color={job?.is_active ? "#38E54D" : "#FF1E1E"} />
                  {job?.staff_name}
                </button>
              </li>
              
              <li className="list-group-item d-flex align-items-center option-item-assign p-1 m-0 fs-7">
                <Form.Control
                  type="text"
                  as="textarea"
                  {...register(`jobs.${index}.assign_reason`)}
                  className="text-uppercase pt-1"
                  style={{ 
                    resize: 'none', 
                    width: '400px', 
                    height: '30px', 
                    minHeight: '30px', 
                    fontSize: '1.2em', 
                    borderRadius: '5px' 
                  }}
                  value={job?.assign_reason || ''}
                  onChange={(e) => handleReasonChange(index, e.target.value)}
                  isInvalid={!!errors.jobs?.[index]?.assign_reason}
                />
              </li>
            </ul>
          );
        })}
      </div>
      
      <div className="d-flex flex-row justify-content-end">
        {/* Multi-selection switch with tooltip */}
        <CustomeToolTip disableInteractive placement='top' title='Multy Selection'>
            <div className="me-2" style={{ position: 'relative' }}>
            <Form.Check
                type="switch"
                reverse={true}
                onChange={(e) => {
                const isChecked = e.target.checked;
                
                if (isChecked) {
                    // Show popup when switching on
                    setShowMultiSelectionPopup(true);
                } else {
                    // Disable multi-selection when switching off
                    setAssignlargeBatchMulty(false);
                    const currentJobs = getValues('jobs');
                    const updated = currentJobs.map((v) => ({
                    ...v,
                    is_select: false,
                    }));
                    setValue('jobs', updated);
                }
                }}
                checked={assignlargeBatchMulty}
                id="multySelection"
                name="multySelection"
            />
            </div>
        </CustomeToolTip>
        {/* Multi-Selection Popup */}
        {showMultiSelectionPopup && (
          <div 
            className="position-fixed d-flex align-items-center justify-content-center"
            style={{
              top: '0',
              left: '0',
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 9999
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-lg p-4"
              style={{
                minWidth: '350px',
                border: '2px solid #007bff',
                borderRadius: '12px',
                position: 'relative'
              }}
            >
              {/* Close Button */}
              <button
                type="button"
                className="btn-close"
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'none',
                  border: 'none',
                  fontSize: '18px',
                  cursor: 'pointer',
                  color: '#6c757d',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setShowMultiSelectionPopup(false)}
                onMouseEnter={(e) => {
                  e.target.style.color = '#000';
                }}
                onMouseLeave={(e) => {
                  e.target.style.color = '#6c757d';
                }}
              >
                ×
              </button>
              
              <div className="text-center mb-4">
                <h5 className="mb-0 text-primary font-weight-bold">Assign Option</h5>
              </div>
              
              <div className="d-flex justify-content-center gap-3">
                <div 
                  className="text-center cursor-pointer p-3 rounded border hover-shadow"
                  style={{
                    border: '2px solid #dee2e6',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleOptionSelect('common')}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#007bff';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#dee2e6';
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  <button 
                    type="button" 
                    className="btn btn-light btn-sm btn-icon btn-active-primary width-common-btn fs-7"
                    disabled
                    style={{ pointerEvents: 'none' }}
                  >
                    Common Pool
                  </button>
                </div>
                
                <div 
                  className="text-center cursor-pointer p-3 rounded border hover-shadow"
                  style={{
                    border: '2px solid #dee2e6',
                    borderRadius: '8px',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleOptionSelect('user')}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#007bff';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = '#dee2e6';
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  <button 
                    type="button" 
                    className="btn btn-light btn-active-primary btn-sm btn-icon width-user-btn"
                    style={{ fontSize: '0.8em', pointerEvents: 'none' }}
                    disabled
                  >
                    {'User'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <button 
          type="submit" 
          disabled={!isValid || isSubmitting}
          className="btn btn-reject text-end me-4"
          data-bs-display="static" 
          aria-expanded="false"
        >
          {isSubmitting ? 'Assigning...' : 'Assign'}
        </button>
      </div>
    </Form>
  );
};

export default AssignBackPopover;